import { ForwardedRef, ReactNode } from 'react';
import { LoggableProps } from '@direct-frontend/modules-logger';
import { ValueOf } from '@direct-frontend/stdlib';

import { CardPaddingCorrection } from '../Card';
import { OverlayProps } from '../Overlay';

/**
 * Состояния для переключения анимации
 */
export type ModalAnimationState = ValueOf<typeof ANIMATION_STATES>;
/**
 * Состояния для переключения анимации
 */
export const ANIMATION_STATES = {
    NONE: '',
    OPEN: 'open',
    CLOSE: 'close',
    MISCLICK: 'misclick',
} as const;

interface ModalPropsBase extends Omit<OverlayProps, 'onAnimationEnd' | 'autoFocus'> {
    /**
     * Названия классов для элемента
     */
    className?: string;

    /**
     * Название классов для Overlay элемента
     */
    overlayClassName?: string;

    /**
     * Дочерние элементы
     * Кнопка-крестик уже встроена
     * @expectedNode `OverlayContent`
     */
    children?: ReactNode;

    /**
     * Переключение между открытым и закрытым состояниями
     * @default false
     */
    isOpen?: boolean;

    /**
     * Действие, выполняемое при закрытии
     */
    onClose?: () => void;

    /**
     * Признак возможности перемещения мобильной шторки (при выключении шторку нельзя двигать пальцем)
     * @default true
     */
    isDraggableMobile?: boolean;

    /**
     * Действие, выполняемое при завершении анимации
     */
    onAnimationEnd?: (animationState: ModalAnimationState) => void;

    /**
     * Используется ли кнопка-крестик для закрытия компонента
     * @default false
     */
    hasCloseButton?: boolean;

    /**
     * Используется ли кнопка-крестик на Overlay для закрытия компонента
     * @default false
     */
    hasCloseButtonOnOverlay?: boolean;

    /**
     * Требуется ли закрывать компонент при клике на затемнённый фон (overlay)
     * (при использовании onAnimationEnd стоит учесть состояние "misclick")
     * @default true
     */
    dismissOnOutsideClick?: boolean;

    /**
     * Включает стили с overflow-y: auto для Modal
     * @default true
     */
    scrollable?: boolean;

    /**
     * Названия классов для кнопки-крестика
     */
    closeButtonClassName?: string;

    /**
     * Названия классов для контейнера модального окна
     */
    containerClassName?: string;

    /**
     * Кнопки внизу модалки
     * @deprecated Стоит использовать отдельный компонент ModalButtons внутри Modal для задания кнопок
     */
    bottomButtons?: ReactNode;

    /**
     * Расположение кнопок внизу модалки (работает только в мобильной версии)
     * @default horizontal
     * @deprecated Стоит использовать свойство type у компонента ModalButtons
     */
    bottomButtonsType?: 'horizontal' | 'vertical';

    /**
     * Позиция контента Overlay, влияет на позиционирование в desktop версии
     * @default top
     */
    position?: 'top' | 'center';

    /**
     * Корректировка отступов сверху и снизу.
     * Используется, когда требуется сделать верхний либо верхний и нижний отступ меньше, чем по бокам.
     * @default top
     */
    paddingCorrection?: CardPaddingCorrection;

    /**
     * Ссылка на DOM-узел с контейнером Modal
     */
    containerRef?: ForwardedRef<HTMLDivElement>;

    /**
     * Ссылка на DOM-узел с контентом Modal
     */
    contentRef?: ForwardedRef<HTMLDivElement>;

    /**
     * Контейнер отрисовки модального окна
     */
    portalContainer?: HTMLElement;

    /**
     * Состояние загрузки, поверх контента показывается паранжа
     */
    isLoading?: boolean;

    /**
     * Сайдбар с серым фоном внутри модального окна с левой стороны.
     * Размер задается по содержимому
     */
    leftbar?: ReactNode;

    /**
     * Сайдбар с серым фоном внутри модального окна с правой стороны.
     * Размер задается по содержимому
     */
    rightbar?: ReactNode;

    /**
     * Флаг отключения паддингов в модалке
     * @default false
     */
    withoutPaddings?: boolean;

    /**
     * Открывать мобильную версию модального окна во весь экран
     * @default false
     */
    isFullscreenMobile?: boolean;

    /**
     * Растянуть модалку по высоте на всю доступную высоту viewport (только для desktop).
     * По аналогии с width='max'.
     */
    height?: 'max';
}

type ModalPropsWithoutWidth = ModalPropsBase & {
    width?: never;
};

export type ModalPropsWithMaxWidth = ModalPropsBase & {
    width: 'max';
    /**
     * Отключить ограничение максимальной ширины модалки значением 1600px (только для desktop).
     * Применяется только когда width='max'.
     */
    maxWidthNoLimit?: boolean;
};

export type ModalPropsRaw = ModalPropsWithoutWidth | ModalPropsWithMaxWidth;

export type ModalProps = LoggableProps<ModalPropsRaw>;
